import { useEffect, useMemo, useState } from 'react';
import { cachedGet, REFERENCE_URLS } from '../offline/offlineApi';
import {
  GUEST_SYSTEM_KEY, normalizeParticipantGroups,
} from '../util/participantGroups';

// One shared list for the whole app (dots, chips, price tables): fetched at most once per page load and
// kept in this module, so lists with many rows never trigger per-row requests. Archived groups are
// included because participants keep their snapshot group; pickers use `active`.
// Offline: read through the reference cache (prefetched on the home page).
let shared = null;      // normalized list once loaded
let pending = null;     // in-flight promise
const listeners = new Set();

function publish(list) {
  shared = list;
  listeners.forEach(fn => fn(list));
}

function load() {
  if (shared) return Promise.resolve(shared);
  if (!pending) {
    pending = cachedGet(REFERENCE_URLS.participantGroups)
      .then(data => {
        const list = normalizeParticipantGroups(data);
        publish(list);
        return list;
      })
      .finally(() => { pending = null; });
  }
  return pending;
}

// After admin changes (/groups): replace the shared list so every mounted consumer re-renders.
export function setParticipantGroups(rawList) {
  publish(normalizeParticipantGroups(rawList));
}

// Forget the list; the next consumer loads it again.
export function invalidateParticipantGroups() {
  shared = null;
}

// Home page prefetch (offline copy, offlineApi.prefetchReferenceData): runs through the shared loader, so
// it reuses the request of a mounted consumer instead of fetching in parallel. cachedGet stores the offline
// copy; once the list is loaded in this page load, that copy is already current.
export function preloadParticipantGroups() {
  return load().catch(() => null);
}

// Back online after "Erneut verbinden" (OfflineContext): load the list again and publish it to every
// mounted consumer. On failure the previous list stays on screen and the next consumer retries.
export function reloadParticipantGroups() {
  shared = null;
  return load().catch(() => null);
}

/**
 * Participant groups for display and selection.
 * - groups: all groups incl. archived, display order (SortOrder, name)
 * - active: non-archived groups (pickers, price tables)
 * - byId: Map id → group
 * - guest: the system group (null before loading)
 */
export default function useParticipantGroups() {
  const [list, setList] = useState(shared);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    const listener = (next) => {
      if (!alive) return;
      setList(next);
      setFailed(false);
    };
    listeners.add(listener);
    if (shared) setList(shared);
    else load().catch(() => { if (alive) setFailed(true); });
    return () => {
      alive = false;
      listeners.delete(listener);
    };
  }, []);

  return useMemo(() => {
    const groups = list ?? [];
    const byId = new Map(groups.map(g => [g.id, g]));
    return {
      groups,
      active: groups.filter(g => !g.isArchived),
      byId,
      guest: groups.find(g => g.systemKey === GUEST_SYSTEM_KEY) ?? null,
      loaded: list != null,
      failed,
    };
  }, [list, failed]);
}
